<template>
  <main class="sf-root">

    <!-- ══════════════════════════════════════════════════
         LOADING
    ══════════════════════════════════════════════════ -->
    <div v-if="loading" class="sf-loader">
      <span class="sf-loader__ring"></span>
    </div>

    <template v-else>

      <!-- ══════════════════════════════════════════════════
           HERO
      ══════════════════════════════════════════════════ -->
      <section class="sf-hero" ref="heroSection">
        <!-- Parallax image layer -->
        <div
          class="sf-hero__bg"
          ref="heroBg"
          :style="{ transform: `translateY(${parallaxOffset}px) scale(1.08)` }"
        >
          <img :src="hero" alt="" loading="eager" />
          <div class="sf-hero__bg-grain"></div>
        </div>

        <!-- Dark vignette overlay -->
        <div class="sf-hero__vignette"></div>

        <!-- Decorative vertical label -->
        <div class="sf-hero__side-label hero-side" aria-hidden="true">
          <span>Fondation Stag — depuis 2024</span>
        </div>

        <!-- Main content -->
        <div class="sf-hero__content">
          <!-- Headline -->
          <h1 class="sf-hero__headline hero-headline">
            Construisons<br />
            <em>ensemble</em><br />
            un avenir<br />
            numérique<br />
            inclusif
          </h1>

          <!-- Sub -->
          <p class="sf-hero__sub hero-sub">
            Stag Fondation connecte les associations aux communautés,
            installe Internet dans les quartiers défavorisés et forme
            les jeunes aux métiers de l'IT.
          </p>

          <!-- CTAs -->
          <div class="sf-hero__actions hero-cta">
            <router-link to="/faire-un-don">
              <button class="sf-btn sf-btn--accent">
                Faire un don
                <ArrowRight class="sf-btn__icon" />
              </button>
            </router-link>
            <router-link to="/formations">
              <button class="sf-btn sf-btn--ghost">
                Nos formations
              </button>
            </router-link>
          </div>
        </div>

        <!-- Stats ribbon (bottom of hero) - using real stats from store -->
        <div class="sf-hero__stats hero-stats" ref="statsBlock">
          <div class="sf-stat">
            <span class="sf-stat__num">{{ formatNumber(displayStats.graduates) }}</span>
            <span class="sf-stat__sep">—</span>
            <span class="sf-stat__label">jeunes formés</span>
          </div>
          <div class="sf-stat__divider"></div>
          <div class="sf-stat">
            <span class="sf-stat__num">{{ formatNumber(displayStats.partners) }}</span>
            <span class="sf-stat__sep">—</span>
            <span class="sf-stat__label">associations partenaires</span>
          </div>
          <div class="sf-stat__divider"></div>
          <div class="sf-stat">
            <span class="sf-stat__num">{{ formatNumber(displayStats.beneficiaries) }}</span>
            <span class="sf-stat__sep">—</span>
            <span class="sf-stat__label">bénéficiaires</span>
          </div>
        </div>

        <!-- Scroll cue -->
        <div class="sf-hero__scroll-cue hero-scroll" aria-hidden="true">
          <span></span>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════
           MISSION STRIP
      ══════════════════════════════════════════════════ -->
      <section class="sf-mission">
        <div class="sf-mission__inner">
          <p
            class="sf-mission__quote"
            data-aos="fade-up"
            data-aos-duration="900"
          >
            « La technologie ne doit pas être un privilège.
            Nous travaillons chaque jour pour qu'elle devienne
            un droit universel. »
          </p>
          <div class="sf-mission__rule" data-aos="scale-x" data-aos-duration="800" data-aos-delay="200"></div>
          <p class="sf-mission__attr" data-aos="fade-up" data-aos-delay="300">
            — Stag Fondation, Manifeste 2024
          </p>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════
           DOMAINES D'ACTION (Features)
      ══════════════════════════════════════════════════ -->
      <section class="sf-features">
        <div class="sf-section-header" data-aos="fade-up" data-aos-duration="700">
          <span class="sf-label">Ce que nous faisons</span>
          <h2 class="sf-h2">Nos domaines<br /><em>d'action</em></h2>
        </div>

        <div class="sf-features__grid">
          <div
            v-for="(feature, i) in features"
            :key="i"
            class="sf-feature-card"
            data-aos="fade-up"
            data-aos-duration="600"
            :data-aos-delay="i * 90"
          >
            <div class="sf-feature-card__number">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="sf-feature-card__icon">
              <component :is="getIconComponent(feature.icon)" />
            </div>
            <h3 class="sf-feature-card__title">{{ feature.title }}</h3>
            <p class="sf-feature-card__desc">{{ feature.description }}</p>
            <div class="sf-feature-card__line"></div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════
           PROJETS RÉCENTS
      ══════════════════════════════════════════════════ -->
      <section class="sf-projects">
        <div class="sf-projects__header">
          <div class="sf-section-header" data-aos="fade-right" data-aos-duration="700">
            <span class="sf-label">Sur le terrain</span>
            <h2 class="sf-h2">Nos projets<br /><em>récents</em></h2>
          </div>
          <router-link
            to="/projets"
            class="sf-link-all"
            data-aos="fade-left"
            data-aos-duration="700"
          >
            Tous les projets <ArrowRight class="sf-link-all__icon" />
          </router-link>
        </div>

        <div v-if="!recentProjects.length" class="sf-empty">
          Aucun projet récent à afficher
        </div>

        <div v-else class="sf-projects__grid">
          <div
            v-for="(project, i) in recentProjects"
            :key="project.id"
            class="sf-project-card"
            :class="{ 'sf-project-card--featured': i === 0 }"
            data-aos="fade-up"
            data-aos-duration="650"
            :data-aos-delay="i * 120"
          >
            <div class="sf-project-card__media">
              <img
                v-if="project.featured_image"
                :src="$store.state.imageUrl + project.featured_image"
                :alt="project.title"
                loading="lazy"
              />
              <div v-else class="sf-project-card__media-placeholder">
                <component :is="getIconComponent(project.icon)" />
              </div>
              <span class="sf-project-card__cat">{{ project.category }}</span>
              <span v-if="project.is_featured" class="sf-project-card__badge">Phare</span>
            </div>

            <div class="sf-project-card__body">
              <div class="sf-project-card__loc">
                <MapPin class="sf-project-card__loc-icon" />
                {{ project.location }}
              </div>
              <h3 class="sf-project-card__title">{{ project.title }}</h3>
              <p class="sf-project-card__desc">{{ project.description }}</p>

              <div class="sf-project-card__progress">
                <div class="sf-project-card__progress-bar">
                  <div
                    class="sf-project-card__progress-fill"
                    :style="{ '--target': project.progress + '%' }"
                  ></div>
                </div>
                <span class="sf-project-card__progress-pct">{{ project.progress }}%</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════
           TÉMOIGNAGES (from store with real reviews)
      ══════════════════════════════════════════════════ -->
      <section class="sf-testimonials">
        <div class="sf-testimonials__bg-text" aria-hidden="true">Voix</div>

        <div class="sf-section-header sf-section-header--light" data-aos="fade-up" data-aos-duration="700">
          <span class="sf-label sf-label--light">Ils témoignent</span>
          <h2 class="sf-h2 sf-h2--light">Des histoires<br /><em>qui changent</em></h2>
        </div>

        <!-- Avis depuis la BD -->
        <div v-if="featuredReviews.length" class="sf-testimonials__grid">
          <div
            v-for="(t, i) in featuredReviews"
            :key="t.id"
            class="sf-tcard"
            data-aos="fade-up"
            data-aos-duration="700"
            :data-aos-delay="i * 130"
          >
            <Quote class="sf-tcard__quote-icon" />
            <!-- Étoiles -->
            <div class="sf-tcard__stars">
              <span v-for="s in 5" :key="s" class="sf-tcard__star" :class="{ 'sf-tcard__star--lit': s <= t.rating }">★</span>
            </div>
            <blockquote class="sf-tcard__text">{{ t.content }}</blockquote>
            <footer class="sf-tcard__footer">
              <div class="sf-tcard__avatar">{{ t.initials || getInitials(t.name) }}</div>
              <div>
                <p class="sf-tcard__name">{{ t.name }}</p>
                <p class="sf-tcard__role">{{ t.role || t.organisation || sourceLabel(t.source_type) }}</p>
              </div>
            </footer>
          </div>
        </div>

        <!-- Fallback : témoignages statiques si la BD est vide -->
        <div v-else class="sf-testimonials__grid">
          <div
            v-for="(t, i) in testimonialsFallback"
            :key="i"
            class="sf-tcard"
            data-aos="fade-up"
            data-aos-duration="700"
            :data-aos-delay="i * 130"
          >
            <Quote class="sf-tcard__quote-icon" />
            <blockquote class="sf-tcard__text">{{ t.content }}</blockquote>
            <footer class="sf-tcard__footer">
              <div class="sf-tcard__avatar">{{ getInitials(t.name) }}</div>
              <div>
                <p class="sf-tcard__name">{{ t.name }}</p>
                <p class="sf-tcard__role">{{ t.role }}</p>
              </div>
            </footer>
          </div>
        </div>

        <!-- Bouton laisser un avis -->
        <div style="text-align:center;margin-top:2.5rem" data-aos="fade-up">
          <button class="sf-btn sf-btn--ghost" @click="showReviewModal=true">
            <MessageSquare class="sf-btn__icon" /> Laisser un témoignage
          </button>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════
           ACTUALITÉS
      ══════════════════════════════════════════════════ -->
      <section class="sf-news">
        <div class="sf-news__header">
          <div class="sf-section-header" data-aos="fade-right" data-aos-duration="700">
            <span class="sf-label">Restez informés</span>
            <h2 class="sf-h2">Dernières<br /><em>actualités</em></h2>
          </div>
          <router-link
            to="/actualites"
            class="sf-link-all"
            data-aos="fade-left"
            data-aos-duration="700"
          >
            Toutes les actualités <ArrowRight class="sf-link-all__icon" />
          </router-link>
        </div>

        <div v-if="!latestArticles.length" class="sf-empty">
          Aucun article récent à afficher
        </div>

        <div v-else class="sf-news__grid">
          <article
            v-for="(article, i) in latestArticles"
            :key="article.id"
            class="sf-news-card"
            data-aos="fade-up"
            data-aos-duration="650"
            :data-aos-delay="i * 100"
          >
            <div class="sf-news-card__media">
              <img
                v-if="article.featured_image"
                :src="$store.state.imageUrl + article.featured_image"
                :alt="article.title"
                loading="lazy"
              />
              <div v-else class="sf-news-card__media-placeholder">
                <Calendar />
              </div>
              <span class="sf-news-card__cat">{{ article.category }}</span>
            </div>
            <div class="sf-news-card__body">
              <div class="sf-news-card__meta">
                <Calendar class="sf-news-card__meta-icon" />
                {{ formatDate(article.published_at || article.created_at) }}
                <span class="sf-news-card__meta-dot">·</span>
                {{ article.reading_time_minutes || 5 }} min
              </div>
              <h3 class="sf-news-card__title">{{ article.title }}</h3>
              <p class="sf-news-card__excerpt">
                {{ article.excerpt || truncateText(article.content, 110) }}
              </p>
              <router-link :to="`/actualite/${article.slug}`" class="sf-news-card__read">
                Lire la suite <ArrowRight class="sf-news-card__read-icon" />
              </router-link>
            </div>
          </article>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════
           CTA FINAL
      ══════════════════════════════════════════════════ -->
      <section class="sf-cta">
        <div class="sf-cta__texture" aria-hidden="true"></div>
        <div class="sf-cta__inner">
          <p class="sf-eyebrow sf-eyebrow--light" data-aos="fade-up" data-aos-duration="600">
            <span class="sf-eyebrow__dot sf-eyebrow__dot--amber"></span>
            Rejoignez la mission
          </p>
          <h2 class="sf-cta__headline" data-aos="fade-up" data-aos-delay="100" data-aos-duration="700">
            Chaque geste compte.<br /><em>Le vôtre aussi.</em>
          </h2>
          <p class="sf-cta__sub" data-aos="fade-up" data-aos-delay="200" data-aos-duration="700">
            Ensemble, nous pouvons transformer la vie de milliers de jeunes
            à travers la technologie et l'éducation numérique.
          </p>

          <div class="sf-cta__cards" data-aos="fade-up" data-aos-delay="300" data-aos-duration="700">
            <router-link to="/faire-un-don" class="sf-cta-card">
              <div class="sf-cta-card__icon-wrap sf-cta-card__icon-wrap--amber">
                <Heart />
              </div>
              <h3>Faire un don</h3>
              <p>Soutenez nos projets et aidez-nous à former plus de jeunes aux métiers du numérique.</p>
              <button class="sf-btn sf-btn--amber sf-cta-card__btn">
                Contribuer maintenant <ArrowRight class="sf-btn__icon" />
              </button>
            </router-link>

            <router-link to="/contact" class="sf-cta-card">
              <div class="sf-cta-card__icon-wrap sf-cta-card__icon-wrap--cream">
                <Users />
              </div>
              <h3>Devenir partenaire</h3>
              <p>Associations, rejoignez Stag Connect et amplifiez votre impact social ensemble.</p>
              <button class="sf-btn sf-btn--cream sf-cta-card__btn">
                Contactez-nous <ArrowRight class="sf-btn__icon" />
              </button>
            </router-link>
          </div>
        </div>
      </section>

    </template>

    <!-- ═══════════ MODALE : Laisser un avis ═══════════ -->
    <Transition name="modal-fade">
      <div v-if="showReviewModal" class="rv-overlay" @click.self="showReviewModal=false">
        <div class="rv-modal">
          <div class="rv-modal__head">
            <h3>Laisser un témoignage</h3>
            <button @click="showReviewModal=false"><X /></button>
          </div>

          <template v-if="!reviewSent">
            <form @submit.prevent="submitReview" class="rv-modal__body">
              <p class="rv-intro">Votre expérience avec Stag Fondation compte. Votre témoignage sera publié après validation.</p>

              <!-- Étoiles interactives -->
              <div class="rv-field">
                <label class="rv-label">Votre note <span>*</span></label>
                <div class="rv-rating">
                  <button v-for="n in 5" :key="n" type="button" class="rv-star-btn" :class="{ 'rv-star-btn--lit': n <= reviewForm.rating }" @click="reviewForm.rating=n" @mouseover="hoverRating=n" @mouseleave="hoverRating=0">
                    <span :class="{ 'lit': n <= (hoverRating||reviewForm.rating) }">★</span>
                  </button>
                  <span class="rv-rating__label">{{ ratingLabel }}</span>
                </div>
              </div>

              <div class="rv-grid">
                <div class="rv-field"><label class="rv-label">Prénom / Nom <span>*</span></label><input v-model="reviewForm.name" required class="sf-input" placeholder="Aminata K." /></div>
                <div class="rv-field"><label class="rv-label">Rôle / Poste</label><input v-model="reviewForm.role" class="sf-input" placeholder="Développeuse Web, Formateur…" /></div>
                <div class="rv-field" style="grid-column:span 2"><label class="rv-label">Organisation</label><input v-model="reviewForm.organisation" class="sf-input" placeholder="Association, école…" /></div>
              </div>

              <div class="rv-field">
                <label class="rv-label">Profil <span>*</span></label>
                <div class="rv-source-grid">
                  <button v-for="s in sources" :key="s.value" type="button" class="rv-source-btn" :class="{ 'rv-source-btn--active': reviewForm.source_type===s.value }" @click="reviewForm.source_type=s.value">
                    {{ s.label }}
                  </button>
                </div>
              </div>

              <div class="rv-field">
                <label class="rv-label">Témoignage <span>*</span></label>
                <textarea v-model="reviewForm.content" required rows="4" class="sf-input" style="resize:vertical;min-height:110px" placeholder="Partagez votre expérience avec Stag Fondation (20 à 600 caractères)…"></textarea>
                <span class="rv-char-count" :class="{ 'rv-char-count--warn': reviewForm.content.length>500 }">{{ reviewForm.content.length }}/600</span>
              </div>

              <p v-if="reviewError" class="rv-error">{{ reviewError }}</p>
            </form>
            <div class="rv-modal__foot">
              <button class="sf-btn sf-btn--outline rv-cancel" @click="showReviewModal=false">Annuler</button>
              <button class="sf-btn sf-btn--accent" :disabled="reviewLoading || reviewForm.rating===0 || !reviewForm.content || !reviewForm.name" @click="submitReview">
                <span v-if="reviewLoading" style="width:16px;height:16px;border-radius:50%;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;animation:spin .8s linear infinite;display:inline-block"></span>
                Envoyer mon témoignage
              </button>
            </div>
          </template>

          <!-- Confirmation après envoi -->
          <div v-else class="rv-modal__body rv-success">
            <div class="rv-success__icon">✓</div>
            <h4>Merci {{ reviewForm.name }} !</h4>
            <p>Votre témoignage a bien été envoyé. Il sera publié après validation par notre équipe.</p>
            <button class="sf-btn sf-btn--accent" @click="showReviewModal=false;reviewSent=false">Fermer</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Toast -->
    <Transition name="toast">
      <div v-if="toastMessage" class="sf-toast">
        <CheckCircle class="sf-toast__icon" />
        {{ toastMessage }}
      </div>
    </Transition>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import AOS from 'aos'
import 'aos/dist/aos.css'
import {
  Heart, Users, ArrowRight, Wifi, BookOpen, Quote,
  MapPin, Calendar, TrendingUp,
  Globe, CheckCircle, MessageSquare, X
} from 'lucide-vue-next'
 
function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3) }
 
function animateCounter(end, duration, onUpdate) {
  const start = performance.now()
  const raf = (now) => {
    const p = Math.min((now - start) / duration, 1)
    onUpdate(Math.round(end * easeOutCubic(p)))
    if (p < 1) requestAnimationFrame(raf)
  }
  requestAnimationFrame(raf)
}
 
export default {
  name: 'HomePage',
  components: {
    Heart, Users, ArrowRight, Wifi, BookOpen, Quote,
    MapPin, Calendar, TrendingUp,
    Globe, CheckCircle, MessageSquare, X
  },
 
  data() {
    return {
      toastMessage: '',
      hero: require('@/assets/hero.png'),
      parallaxOffset: 0,
      _scrollHandler: null,
      _statsObserver: null,
      _countersStarted: false,
 
      // Compteurs animés — alimentés par homeStats de la BD
      displayStats: { graduates: 0, partners: 0, beneficiaries: 0 },
 
      // Modale avis
      showReviewModal: false,
      reviewSent: false,
      reviewLoading: false,
      reviewError: '',
      hoverRating: 0,
      reviewForm: { name: '', role: '', organisation: '', content: '', rating: 5, source_type: 'autre' },
      sources: [
        { value: 'beneficiaire', label: 'Bénéficiaire' },
        { value: 'partenaire',   label: 'Partenaire' },
        { value: 'formateur',    label: 'Formateur' },
        { value: 'donateur',     label: 'Donateur' },
        { value: 'autre',        label: 'Autre' },
      ],
 
      features: [
        { icon: 'Wifi',       title: 'Accès Internet',      description: "Installation d'antennes Internet dans les quartiers défavorisés pour réduire la fracture numérique." },
        { icon: 'BookOpen',   title: 'Formations IT',        description: 'Programmes de formation aux métiers du numérique : développement web, cybersécurité, data science.' },
        { icon: 'Users',      title: 'Stag Connect',         description: 'Plateforme qui connecte les associations aux communautés et facilite les actions sociales.' },
        { icon: 'Heart',      title: 'Impact Social',        description: 'Projets environnementaux et sociaux en collaboration avec des associations locales.' },
        { icon: 'Globe',      title: 'Réseau Solidaire',     description: "Construction d'un écosystème de partenaires engagés pour un impact durable." },
        { icon: 'TrendingUp', title: 'Croissance Inclusive', description: "Accompagnement des jeunes vers l'emploi et l'entrepreneuriat numérique." },
      ],
 
      testimonialsFallback: [
        { name: 'Aminata K.',         role: 'Bénéficiaire — Développeuse Web',  content: "Grâce à Stag Fondation, j'ai pu suivre une formation en développement web. Aujourd'hui je travaille dans une startup tech. Ma vie a complètement changé." },
        { name: 'Association Espoir', role: 'Partenaire',                        content: "Stag Connect nous a permis de toucher plus de personnes. C'est un outil indispensable pour notre association." },
        { name: 'Dylan M.',           role: 'Formateur IT',                      content: 'Enseigner aux jeunes de mon quartier est une expérience enrichissante. Voir leur progression me motive chaque jour.' },
      ]
    }
  },
 
  computed: {
    ...mapState({
      loading:         s => s.loading,
      recentProjects:  s => s.projects?.slice(0, 3) || [],
      latestArticles:  s => s.articles?.slice(0, 3) || [],
      featuredReviews: s => s.featuredReviews || [],
      homeStats:       s => s.homeStats || {},
    }),
    ratingLabel() {
      return ['' ,'Mauvais','Moyen','Bien','Très bien','Excellent !'][this.reviewForm.rating] || ''
    },
  },
 
  created() { this.loadHomeData() },
 
  mounted() {
    AOS.init({ once: true, offset: 70, easing: 'ease-out-cubic', duration: 650 })
    this._scrollHandler = () => { this.parallaxOffset = window.scrollY * 0.35 }
    window.addEventListener('scroll', this._scrollHandler, { passive: true })
    this._runHeroEntrance()
    this.$nextTick(() => this._observeStats())
  },
 
  beforeUnmount() {
    window.removeEventListener('scroll', this._scrollHandler)
    if (this._statsObserver) this._statsObserver.disconnect()
  },
 
  watch: {
    homeStats(v) {
      if (v && !this._countersStarted) this._maybeStartCounters()
    }
  },
 
  methods: {
    // ✅ FIX 1 : mapActions en OBJET avec alias storeSubmitReview → évite la collision
    // avec la méthode locale submitReview() qui portait le même nom.
    // Avant : ...mapActions([..., 'submitReview'])  ← écrasait la méthode locale
    // Après : storeSubmitReview: 'submitReview'     ← alias distinct
    ...mapActions({
      fetchFeaturedProject: 'fetchFeaturedProject',
      fetchPublicProjects:  'fetchPublicProjects',
      fetchPublicArticles:  'fetchPublicArticles',
      fetchFeaturedReviews: 'fetchFeaturedReviews',
      fetchHomeStats:       'fetchHomeStats',
      storeSubmitReview:    'submitReview',
    }),
 
    async loadHomeData() {
      try {
        await Promise.all([
          this.fetchFeaturedProject(),
          this.fetchPublicProjects({ per_page: 3 }),
          this.fetchPublicArticles({ per_page: 3 }),
          this.fetchFeaturedReviews(),
          this.fetchHomeStats(),
        ])
        this.$nextTick(() => { AOS.refresh(); this._maybeStartCounters() })
      } catch {
        this.toastMessage = 'Erreur lors du chargement des données'
      }
    },
 
    _runHeroEntrance() {
      const targets = [
        { sel: '.hero-headline', delay: 250 },
        { sel: '.hero-sub',      delay: 420 },
        { sel: '.hero-cta',      delay: 560 },
        { sel: '.hero-stats',    delay: 680 },
        { sel: '.hero-side',     delay: 800 },
        { sel: '.hero-scroll',   delay: 900 }
      ]
      targets.forEach(({ sel, delay }) => {
        const el = document.querySelector(sel)
        if (!el) return
        Object.assign(el.style, { opacity: '0', transform: 'translateY(24px)', transition: 'opacity .75s ease, transform .75s ease' })
        setTimeout(() => Object.assign(el.style, { opacity: '1', transform: 'translateY(0)' }), delay)
      })
    },
 
    _observeStats() {
      const el = this.$refs.statsBlock
      if (!el) return
      this._statsObserver = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) { this._maybeStartCounters(); this._statsObserver.disconnect() }
      }, { threshold: 0.5 })
      this._statsObserver.observe(el)
    },
 
    _maybeStartCounters() {
      if (this._countersStarted) return
      const s = this.homeStats
      if (!s.graduates && !s.partners && !s.beneficiaries) return
      this._countersStarted = true
      animateCounter(s.graduates     || 0, 1800, v => { this.displayStats.graduates     = v })
      animateCounter(s.partners      || 0, 1400, v => { this.displayStats.partners      = v })
      animateCounter(s.beneficiaries || 0, 1600, v => { this.displayStats.beneficiaries = v })
    },
 
    async submitReview() {
      if (!this.reviewForm.name || !this.reviewForm.content || !this.reviewForm.rating) return
      if (this.reviewForm.content.length < 20) {
        this.reviewError = 'Témoignage trop court (minimum 20 caractères).'
        return
      }
      this.reviewLoading = true
      this.reviewError = ''
      try {
        // ✅ FIX 2 : appel de l'alias storeSubmitReview au lieu de this.submitReview()
        // Avant : await this.submitReview(...)  ← récursion infinie
        // Après : await this.storeSubmitReview(...)  ← appelle l'action Vuex
        await this.storeSubmitReview(this.reviewForm)
        this.reviewSent = true
      } catch (e) {
        this.reviewError = e.response?.data?.message || "Erreur lors de l'envoi. Réessayez."
      } finally {
        this.reviewLoading = false
      }
    },
 
    getIconComponent(name) {
      return { Wifi, BookOpen, Users, Heart, Globe, TrendingUp }[name] || BookOpen
    },
    getInitials(name) {
      return name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?'
    },
    sourceLabel(s) {
      return { beneficiaire: 'Bénéficiaire', partenaire: 'Partenaire', formateur: 'Formateur', donateur: 'Donateur', autre: 'Contributeur' }[s] || 'Contributeur'
    },
    formatNumber(n) {
      return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n || 0)
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : 'N/A'
    },
    truncateText(t, l) {
      return t?.length > l ? t.slice(0, l) + '…' : (t || '')
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500&display=swap');

/* ── Design tokens ────────────────────────────────────────────────────────── */
.sf-root {
  --cream:    #F5EFE0;
  --cream-d:  #EDE4CC;
  --forest:   #1B3A2D;
  --forest-l: #244D3C;
  --terra:    #C85A2A;
  --amber:    #E8933A;
  --ink:      #1A1A18;
  --ink-60:   rgba(26,26,24,.6);
  --ink-20:   rgba(26,26,24,.12);
  --sage:     #7A9E87;
  --white:    #FFFFFF;
  --fd:       'Cormorant Garamond', Georgia, serif;
  --fb:       'DM Sans', sans-serif;

  font-family: var(--fb);
  background: var(--cream);
  color: var(--ink);
  overflow-x: hidden;
}

/* ── Shared typography ────────────────────────────────────────────────────── */
.sf-label {
  display: inline-block;
  font-size: .72rem;
  font-weight: 500;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: var(--terra);
  margin-bottom: .75rem;
}
.sf-label--light { color: var(--amber); }

.sf-h2 {
  font-family: var(--fd);
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  font-weight: 600;
  line-height: 1.1;
  color: var(--ink);
}
.sf-h2 em { font-style: italic; color: var(--terra); }
.sf-h2--light { color: var(--cream); }
.sf-h2--light em { color: var(--amber); }

.sf-section-header { margin-bottom: 3.5rem; }
.sf-section-header--light { max-width: 1200px; margin: 0 auto 3.5rem; }

.sf-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  font-size: .8rem;
  letter-spacing: .14em;
  text-transform: uppercase;
  font-weight: 500;
  color: var(--cream);
  margin-bottom: 1.25rem;
}
.sf-eyebrow__dot {
  display: inline-block;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--amber);
  flex-shrink: 0;
}

.sf-link-all {
  display: inline-flex;
  align-items: center;
  gap: .4rem;
  font-size: .85rem;
  font-weight: 500;
  letter-spacing: .06em;
  color: var(--terra);
  text-decoration: none;
  transition: gap .25s ease;
  white-space: nowrap;
  align-self: flex-end;
  padding-bottom: .5rem;
}
.sf-link-all:hover { gap: .7rem; }
.sf-link-all__icon { width: 16px; height: 16px; }

.sf-empty {
  text-align: center;
  padding: 4rem;
  color: var(--ink-60);
  font-size: .9rem;
}

/* ── Buttons ──────────────────────────────────────────────────────────────── */
.sf-btn {
  display: inline-flex;
  align-items: center;
  gap: .45rem;
  padding: .8rem 1.6rem;
  border-radius: 4px;
  font-family: var(--fb);
  font-size: .88rem;
  font-weight: 500;
  letter-spacing: .05em;
  cursor: pointer;
  border: none;
  transition: transform .2s ease, box-shadow .2s ease;
}
.sf-btn:hover { transform: translateY(-2px); }
.sf-btn--accent { background: var(--terra); color: var(--white); box-shadow: 0 4px 18px rgba(200,90,42,.35); }
.sf-btn--accent:hover { box-shadow: 0 8px 28px rgba(200,90,42,.45); }
.sf-btn--ghost  { background: rgba(255,255,255,.15); color: var(--white); border: 1px solid rgba(255,255,255,.35); backdrop-filter: blur(6px); }
.sf-btn--ghost:hover  { background: rgba(255,255,255,.25); }
.sf-btn--amber  { background: var(--amber); color: var(--white); }
.sf-btn--cream  { background: var(--cream); color: var(--forest); }
.sf-btn__icon   { width: 16px; height: 16px; flex-shrink: 0; }

/* ── Loader ───────────────────────────────────────────────────────────────── */
.sf-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: var(--cream);
}
.sf-loader__ring {
  width: 44px; height: 44px;
  border-radius: 50%;
  border: 3px solid var(--ink-20);
  border-top-color: var(--terra);
  animation: spin .9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── HERO ─────────────────────────────────────────────────────────────────── */
.sf-hero {
  position: relative;
  height: 100svh;
  min-height: 640px;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
}

.sf-hero__bg {
  position: absolute;
  inset: -8% 0;
  will-change: transform;
  transform-origin: center top;
}
.sf-hero__bg img {
  width: 100%; height: 100%;
  object-fit: cover;
  object-position: center 30%;
  display: block;
}
.sf-hero__bg-grain {
  position: absolute; inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.07'/%3E%3C/svg%3E");
  background-size: 200px;
  pointer-events: none;
  mix-blend-mode: overlay;
  opacity: .5;
}

.sf-hero__vignette {
  position: absolute; inset: 0;
  background:
    linear-gradient(to right, rgba(20,18,12,.82) 0%, rgba(20,18,12,.20) 65%, transparent 100%),
    linear-gradient(to top,   rgba(20,18,12,.92) 0%, transparent 55%);
  z-index: 1;
}

.sf-hero__side-label {
  position: absolute;
  left: 1.25rem; bottom: 11rem;
  z-index: 3;
  writing-mode: vertical-lr;
  transform: rotate(180deg);
  font-size: .65rem;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: rgba(255,255,255,.4);
}

.sf-hero__content {
  position: relative;
  z-index: 3;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 8.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.sf-hero__headline {
  font-family: var(--fd);
  font-size: clamp(3.2rem, 5.5vw, 6.5rem);
  font-weight: 600;
  line-height: 1.0;
  color: var(--white);
  margin: 0 0 1.5rem;
}
.sf-hero__headline em {
  font-style: italic;
  color: var(--amber);
}

.sf-hero__sub {
  max-width: 44ch;
  color: rgba(255,255,255,.75);
  font-size: clamp(.88rem, 1.5vw, 1.05rem);
  line-height: 1.65;
  margin-bottom: 2rem;
}

.sf-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: .75rem;
}

/* Stats ribbon */
.sf-hero__stats {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  background: rgba(27,58,45,.92);
  backdrop-filter: blur(12px);
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(255,255,255,.08);
}

.sf-stat {
  display: flex;
  align-items: baseline;
  gap: .55rem;
  flex: 1;
  justify-content: center;
}
.sf-stat__num {
  font-family: var(--fd);
  font-size: clamp(1.8rem, 3.5vw, 2.8rem);
  font-weight: 700;
  color: var(--amber);
  line-height: 1;
}
.sf-stat__sep   { color: rgba(255,255,255,.25); }
.sf-stat__label { font-size: .78rem; color: rgba(255,255,255,.6); letter-spacing: .06em; }
.sf-stat__divider {
  width: 1px; height: 2.5rem;
  background: rgba(255,255,255,.12);
  flex-shrink: 0;
}

/* Scroll cue */
.sf-hero__scroll-cue {
  position: absolute;
  bottom: 6.5rem; right: 2.5rem;
  z-index: 4;
}
.sf-hero__scroll-cue span {
  display: block;
  width: 1px; height: 60px;
  background: linear-gradient(to bottom, rgba(255,255,255,.6), transparent);
  animation: scrollCue 2s ease-in-out infinite;
}
@keyframes scrollCue {
  0%   { transform: scaleY(0); transform-origin: top; opacity: 1; }
  50%  { transform: scaleY(1); transform-origin: top; }
  100% { transform: scaleY(1); transform-origin: bottom; opacity: 0; }
}

/* ── MISSION ──────────────────────────────────────────────────────────────── */
.sf-mission {
  background: var(--forest);
  padding: 5rem 2rem;
}
.sf-mission__inner {
  max-width: 860px;
  margin: 0 auto;
  text-align: center;
}
.sf-mission__quote {
  font-family: var(--fd);
  font-size: clamp(1.35rem, 2.8vw, 2rem);
  font-style: italic;
  color: var(--cream);
  line-height: 1.55;
  margin-bottom: 1.5rem;
}
.sf-mission__rule {
  width: 4rem; height: 1px;
  background: var(--amber);
  margin: 0 auto 1.25rem;
  transform-origin: left;
}
.sf-mission__attr {
  font-size: .8rem;
  letter-spacing: .1em;
  color: var(--sage);
  text-transform: uppercase;
}

/* ── FEATURES ─────────────────────────────────────────────────────────────── */
.sf-features {
  background: var(--cream);
  padding: 6rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
}
.sf-features__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2px;
}
.sf-feature-card {
  background: var(--white);
  padding: 2.5rem 2rem;
  position: relative;
  overflow: hidden;
}
.sf-feature-card::after {
  content: '';
  position: absolute; inset: 0;
  background: var(--forest);
  transform: scaleY(0);
  transform-origin: bottom;
  transition: transform .45s cubic-bezier(.25,.46,.45,.94);
  z-index: 0;
}
.sf-feature-card:hover::after { transform: scaleY(1); }
.sf-feature-card:hover .sf-feature-card__number,
.sf-feature-card:hover .sf-feature-card__title,
.sf-feature-card:hover .sf-feature-card__desc { color: var(--cream); }
.sf-feature-card:hover .sf-feature-card__icon { color: var(--amber); }
.sf-feature-card:hover .sf-feature-card__line { background: var(--amber); }

.sf-feature-card__number {
  font-family: var(--fd);
  font-size: 3.5rem; font-weight: 700;
  color: var(--cream-d);
  line-height: 1; margin-bottom: .5rem;
  position: relative; z-index: 1;
  transition: color .3s ease;
}
.sf-feature-card__icon {
  width: 26px; height: 26px;
  color: var(--terra);
  margin-bottom: 1rem;
  position: relative; z-index: 1;
  transition: color .3s ease;
}
.sf-feature-card__icon :deep(svg) { width: 100%; height: 100%; }
.sf-feature-card__title {
  font-family: var(--fd);
  font-size: 1.35rem; font-weight: 600;
  color: var(--ink);
  margin-bottom: .6rem;
  position: relative; z-index: 1;
  transition: color .3s ease;
}
.sf-feature-card__desc {
  font-size: .85rem; color: var(--ink-60);
  line-height: 1.6;
  position: relative; z-index: 1;
  transition: color .3s ease;
}
.sf-feature-card__line {
  margin-top: 1.5rem;
  height: 2px; width: 2.5rem;
  background: var(--terra);
  position: relative; z-index: 1;
  transition: background .3s ease;
}

/* ── PROJECTS ─────────────────────────────────────────────────────────────── */
.sf-projects {
  background: var(--cream-d);
  padding: 6rem 2rem;
}
.sf-projects__header {
  max-width: 1200px;
  margin: 0 auto 3.5rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
}
.sf-projects__grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}
.sf-project-card--featured { grid-column: span 2; }

.sf-project-card {
  background: var(--white);
  border-radius: 8px;
  overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 2px 12px rgba(26,26,18,.06);
  transition: transform .3s ease, box-shadow .3s ease;
}
.sf-project-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 36px rgba(26,26,18,.12);
}

.sf-project-card__media {
  position: relative; height: 220px;
  overflow: hidden; background: var(--cream-d);
}
.sf-project-card--featured .sf-project-card__media { height: 300px; }
.sf-project-card__media img {
  width: 100%; height: 100%; object-fit: cover;
  transition: transform .6s ease;
}
.sf-project-card:hover .sf-project-card__media img { transform: scale(1.06); }
.sf-project-card__media-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: var(--sage);
}
.sf-project-card__media-placeholder :deep(svg) { width: 48px; height: 48px; }

.sf-project-card__cat {
  position: absolute; top: 1rem; right: 1rem;
  background: var(--forest); color: var(--cream);
  font-size: .7rem; letter-spacing: .1em; text-transform: uppercase;
  padding: .3rem .7rem; border-radius: 2px;
}
.sf-project-card__badge {
  position: absolute; top: 1rem; left: 1rem;
  background: var(--amber); color: var(--white);
  font-size: .7rem; letter-spacing: .1em; text-transform: uppercase;
  padding: .3rem .7rem; border-radius: 2px;
}

.sf-project-card__body {
  padding: 1.5rem; flex: 1;
  display: flex; flex-direction: column;
}
.sf-project-card__loc {
  display: flex; align-items: center; gap: .35rem;
  font-size: .75rem; color: var(--ink-60); margin-bottom: .6rem;
}
.sf-project-card__loc-icon { width: 13px; height: 13px; flex-shrink: 0; }
.sf-project-card__title {
  font-family: var(--fd);
  font-size: 1.3rem; font-weight: 600; color: var(--ink);
  margin-bottom: .5rem; line-height: 1.2;
}
.sf-project-card__desc {
  font-size: .83rem; color: var(--ink-60); line-height: 1.55;
  flex: 1; margin-bottom: 1.2rem;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}

.sf-project-card__progress { display: flex; align-items: center; gap: .75rem; margin-top: auto; }
.sf-project-card__progress-bar {
  flex: 1; height: 4px; background: var(--cream-d); border-radius: 2px; overflow: hidden;
}
.sf-project-card__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--forest), var(--sage));
  border-radius: 2px;
  width: 0;
  transition: width 1.4s cubic-bezier(.25,.46,.45,.94);
}
[data-aos].aos-animate .sf-project-card__progress-fill {
  width: var(--target, 0%);
}
.sf-project-card__progress-pct {
  font-size: .78rem; font-weight: 500; color: var(--forest); flex-shrink: 0;
}

/* ── TESTIMONIALS ─────────────────────────────────────────────────────────── */
.sf-testimonials {
  background: var(--forest);
  padding: 6rem 2rem;
  position: relative;
  overflow: hidden;
}
.sf-testimonials__bg-text {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  font-family: var(--fd);
  font-size: clamp(10rem, 25vw, 22rem);
  font-weight: 700;
  color: rgba(255,255,255,.03);
  white-space: nowrap;
  pointer-events: none;
  line-height: 1;
  letter-spacing: -.04em;
  user-select: none;
}

.sf-testimonials__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative; z-index: 1;
}

.sf-tcard {
  background: rgba(255,255,255,.05);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 8px;
  padding: 2rem;
  backdrop-filter: blur(4px);
  transition: background .3s ease, border-color .3s ease;
}
.sf-tcard:hover {
  background: rgba(255,255,255,.09);
  border-color: rgba(255,255,255,.2);
}
.sf-tcard__quote-icon { width: 32px; height: 32px; color: var(--amber); margin-bottom: 1.25rem; }
.sf-tcard__stars { display: flex; gap: 2px; margin-bottom: .75rem; }
.sf-tcard__star { font-size: 1.1rem; color: rgba(255,255,255,.2); transition: color .2s; }
.sf-tcard__star--lit { color: var(--amber); }
.sf-tcard__text {
  font-family: var(--fd);
  font-size: 1.1rem;
  font-style: italic;
  color: rgba(245,239,224,.9);
  line-height: 1.6;
  margin-bottom: 1.5rem;

  /* 👇 solution anti-débordement */
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}
.sf-tcard__footer { display: flex; align-items: center; gap: .85rem; }
.sf-tcard__avatar {
  width: 42px; height: 42px; border-radius: 50%;
  background: var(--terra);
  display: flex; align-items: center; justify-content: center;
  color: var(--white); font-weight: 600; font-size: .9rem; flex-shrink: 0;
}
.sf-tcard__name { font-size: .9rem; font-weight: 500; color: var(--cream); }
.sf-tcard__role { font-size: .75rem; color: var(--sage); margin-top: .15rem; }

/* ── NEWS ─────────────────────────────────────────────────────────────────── */
.sf-news {
  background: var(--cream);
  padding: 6rem 2rem;
}
.sf-news__header {
  max-width: 1200px;
  margin: 0 auto 3.5rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem; flex-wrap: wrap;
}
.sf-news__grid {
  max-width: 1200px; margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.sf-news-card {
  background: var(--white);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(26,26,18,.05);
  transition: transform .3s ease, box-shadow .3s ease;
}
.sf-news-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(26,26,18,.1);
}
.sf-news-card__media {
  position: relative; height: 200px; overflow: hidden; background: var(--cream-d);
}
.sf-news-card__media img {
  width: 100%; height: 100%; object-fit: cover;
  transition: transform .6s ease;
}
.sf-news-card:hover .sf-news-card__media img { transform: scale(1.06); }
.sf-news-card__media-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center; color: var(--sage);
}
.sf-news-card__media-placeholder :deep(svg) { width: 40px; height: 40px; }
.sf-news-card__cat {
  position: absolute; top: 1rem; right: 1rem;
  background: var(--forest); color: var(--cream);
  font-size: .7rem; letter-spacing: .1em; text-transform: uppercase;
  padding: .3rem .7rem; border-radius: 2px;
}
.sf-news-card__body { padding: 1.5rem; }
.sf-news-card__meta {
  display: flex; align-items: center; gap: .35rem;
  font-size: .75rem; color: var(--ink-60); margin-bottom: .75rem;
}
.sf-news-card__meta-icon { width: 12px; height: 12px; flex-shrink: 0; }
.sf-news-card__meta-dot { margin: 0 .15rem; }
.sf-news-card__title {
  font-family: var(--fd);
  font-size: 1.25rem; font-weight: 600; color: var(--ink);
  margin-bottom: .6rem; line-height: 1.25;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.sf-news-card__excerpt {
  font-size: .83rem; color: var(--ink-60); line-height: 1.55;
  margin-bottom: 1.1rem;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.sf-news-card__read {
  display: inline-flex; align-items: center; gap: .35rem;
  font-size: .82rem; font-weight: 500;
  color: var(--terra); text-decoration: none;
  transition: gap .2s ease;
}
.sf-news-card__read:hover { gap: .6rem; }
.sf-news-card__read-icon { width: 14px; height: 14px; }

/* ── CTA ──────────────────────────────────────────────────────────────────── */
.sf-cta {
  background: var(--forest-l);
  padding: 7rem 2rem;
  position: relative; overflow: hidden;
}
.sf-cta__texture {
  position: absolute; inset: 0; pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
.sf-cta__inner {
  max-width: 860px; margin: 0 auto;
  text-align: center; position: relative; z-index: 1;
}
.sf-cta__headline {
  font-family: var(--fd);
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 600; line-height: 1.1;
  color: var(--cream); margin-bottom: 1.25rem;
}
.sf-cta__headline em { font-style: italic; color: var(--amber); }
.sf-cta__sub {
  max-width: 52ch; margin: 0 auto 3rem;
  font-size: 1rem; color: rgba(245,239,224,.7); line-height: 1.65;
}
.sf-cta__cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  max-width: 720px;
  margin: 0 auto;
  text-align: left;
}
.sf-cta-card {
  display: flex; flex-direction: column;
  padding: 2rem; border-radius: 8px;
  background: rgba(255,255,255,.07);
  border: 1px solid rgba(255,255,255,.12);
  text-decoration: none; color: inherit;
  transition: background .3s ease, border-color .3s ease, transform .3s ease;
}
.sf-cta-card:hover {
  background: rgba(255,255,255,.12);
  border-color: rgba(255,255,255,.2);
  transform: translateY(-3px);
}
.sf-cta-card__icon-wrap {
  width: 52px; height: 52px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 1.25rem;
}
.sf-cta-card__icon-wrap :deep(svg) { width: 24px; height: 24px; color: var(--white); }
.sf-cta-card__icon-wrap--amber { background: var(--terra); }
.sf-cta-card__icon-wrap--cream { background: rgba(245,239,224,.15); }
.sf-cta-card h3 {
  font-family: var(--fd);
  font-size: 1.4rem; font-weight: 600;
  color: var(--cream); margin-bottom: .65rem;
}
.sf-cta-card p {
  font-size: .85rem; color: rgba(245,239,224,.65);
  line-height: 1.6; margin-bottom: 1.5rem; flex: 1;
}
.sf-cta-card__btn { width: 100%; justify-content: center; margin-top: auto; }

/* ── MODALE AVIS ──────────────────────────────────────────────────────────── */
.rv-overlay { position:fixed; inset:0; background:rgba(0,0,0,.48); display:flex; align-items:center; justify-content:center; z-index:300; padding:1rem; }
.rv-modal { background:var(--white); border-radius:12px; box-shadow:0 24px 60px rgba(0,0,0,.22); max-width:560px; width:100%; max-height:92vh; display:flex; flex-direction:column; font-family:var(--fb); }
.rv-modal__head { display:flex; align-items:center; justify-content:space-between; padding:1.25rem 1.5rem; border-bottom:1px solid rgba(26,26,24,.1); }
.rv-modal__head h3 { font-family:var(--fd); font-size:1.25rem; font-weight:600; color:var(--ink); }
.rv-modal__head button { background:var(--cream); border:none; border-radius:4px; width:28px; height:28px; display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--ink-60); }
.rv-modal__head button :deep(svg) { width:14px; height:14px; }
.rv-modal__body { padding:1.5rem; overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:1rem; }
.rv-modal__foot { padding:1rem 1.5rem; border-top:1px solid rgba(26,26,24,.1); display:flex; justify-content:flex-end; gap:.65rem; }
.rv-intro { font-size:.85rem; color:var(--ink-60); line-height:1.6; }

/* Étoiles interactives */
.rv-rating { display:flex; align-items:center; gap:.25rem; }
.rv-star-btn { background:none; border:none; cursor:pointer; padding:.1rem; font-size:1.8rem; line-height:1; transition:transform .15s ease; color:rgba(26,26,24,.15); }
.rv-star-btn span { transition:color .15s ease; }
.rv-star-btn span.lit { color:var(--amber); }
.rv-star-btn:hover { transform:scale(1.2); }
.rv-rating__label { font-size:.78rem; font-weight:500; color:var(--terra); margin-left:.5rem; }

/* Grille formulaire */
.rv-grid { display:grid; grid-template-columns:1fr 1fr; gap:.85rem; }
@media(max-width:480px) { .rv-grid { grid-template-columns:1fr; } }
.rv-field { display:flex; flex-direction:column; gap:.3rem; }
.rv-label { font-size:.8rem; font-weight:500; color:var(--ink); }
.rv-label span { color:var(--terra); }

/* Boutons source */
.rv-source-grid { display:flex; flex-wrap:wrap; gap:.4rem; margin-top:.25rem; }
.rv-source-btn { padding:.4rem .85rem; border-radius:999px; font-size:.78rem; font-weight:500; cursor:pointer; border:1.5px solid rgba(26,26,24,.12); background:var(--white); color:var(--ink-60); transition:all .2s ease; }
.rv-source-btn:hover { border-color:var(--terra); color:var(--terra); }
.rv-source-btn--active { background:var(--forest); color:var(--white); border-color:var(--forest); }

/* Compteur caractères */
.rv-char-count { font-size:.72rem; color:var(--ink-60); text-align:right; margin-top:.2rem; }
.rv-char-count--warn { color:var(--terra); font-weight:600; }

.rv-error { font-size:.82rem; color:var(--terra); background:rgba(200,90,42,.07); padding:.65rem 1rem; border-radius:4px; }

/* Confirmation */
.rv-success { align-items:center; text-align:center; padding:2rem; }
.rv-success__icon { width:64px; height:64px; border-radius:50%; background:rgba(16,185,129,.12); display:flex; align-items:center; justify-content:center; font-size:1.8rem; color:#10b981; margin:0 auto 1rem; }
.rv-success h4 { font-family:var(--fd); font-size:1.4rem; font-weight:600; color:var(--ink); margin-bottom:.5rem; }
.rv-success p { font-size:.88rem; color:var(--ink-60); line-height:1.65; margin-bottom:1.5rem; max-width:38ch; }

.rv-cancel { font-size:.85rem; }

/* Transitions */
.modal-fade-enter-active, .modal-fade-leave-active { transition:opacity .25s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity:0; }

/* ── TOAST ────────────────────────────────────────────────────────────────── */
.sf-toast {
  position: fixed; bottom: 1.5rem; right: 1.5rem; z-index: 100;
  display: flex; align-items: center; gap: .5rem;
  background: var(--forest); color: var(--cream);
  padding: .85rem 1.4rem; border-radius: 6px;
  font-size: .87rem;
  box-shadow: 0 8px 24px rgba(0,0,0,.25);
}
.sf-toast__icon { width: 18px; height: 18px; color: var(--amber); flex-shrink: 0; }
.toast-enter-active, .toast-leave-active { transition: opacity .3s ease, transform .3s ease; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateY(8px); }

/* ── Responsive ───────────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .sf-projects__grid { grid-template-columns: 1fr 1fr; }
  .sf-project-card--featured { grid-column: span 2; }
}
@media (max-width: 640px) {
  .sf-hero__side-label { display: none; }
  .sf-hero__scroll-cue { display: none; }
  .sf-hero__stats { flex-wrap: wrap; gap: .25rem; padding: 1rem; }
  .sf-stat__divider { display: none; }
  .sf-stat { flex: 1 0 30%; }
  .sf-projects__grid { grid-template-columns: 1fr; }
  .sf-project-card--featured { grid-column: span 1; }
  .sf-cta__cards { grid-template-columns: 1fr; }
  .sf-features__grid { grid-template-columns: 1fr; gap: 1px; }
  .sf-projects__header,
  .sf-news__header { flex-direction: column; align-items: flex-start; }
}

/* ── AOS custom: scale-x ─────────────────────────────────────────────────── */
[data-aos="scale-x"] {
  transform: scaleX(0);
  transform-origin: left;
  transition-property: transform, opacity;
}
[data-aos="scale-x"].aos-animate { transform: scaleX(1); }
</style>